import React from "react";
import { Typography } from "antd";
import { useSelector } from "react-redux";
import moment from "moment";
import commaNumber from "comma-number";
import InstitutionProfile from "../../../../components/common/InstitutionProfile";
import { toCompanyDateOnlyforReportDateFilter } from "../../../../lib/functions";
import "../../../../components/common/bt.css";

const { Title, Text } = Typography;
const format = commaNumber.bindWith(",", ".");

const buildWithDetailsData = (data) => {
  const rows = [];
  let counter = 1;

  data.forEach((voucher) => {
    let subTotal = 0;

    voucher.details.forEach((d, dIdx) => {
      subTotal += parseFloat(d.exp_amount || 0);

      rows.push({
        key: `${voucher.id}-${dIdx}`,
        exp_code: voucher.exp_code,
        created_date: voucher.created_date,
        acc_name: voucher.acc_name,
        full_name: voucher.full_name,
        narration: voucher.narration,
        paymentFor: d.acc_name,
        exp_amount: d.exp_amount,
        isSubtotal: false,
        isFirstDetail: dIdx === 0,
        voucherNo: counter,
      });
    });

    rows.push({
      key: `subtotal-${voucher.id}`,
      isSubtotal: true,
      subTotal,
    });

    counter++;
  });

  return rows;
};

const ExpenseRecordPrintView = React.forwardRef((props, ref) => {
  const {
    filters,
    filterResult,
    filterResultDetails,
    details,
    selectedFilterType,
    selectedRecordType,
  } = useSelector((state) => state.expenseRecord);

  const renderPrintContent = () => {
    if (selectedFilterType?.filterType === "By Expense") {
      const totals = details.reduce(
        (sum, item) => sum + parseFloat(item.exp_amount || 0),
        0
      );

      return (
        <table className="ledger-print-table">
          <colgroup>
            <col style={{ width: "5%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "20%" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "25%" }} />
            <col style={{ width: "15%" }} />
          </colgroup>
          <tbody>
            <tr className="ledger-print-heading">
              <td>No.</td>
              <td>VCH No</td>
              <td>Date</td>
              <td>Payment For</td>
              <td>Payment By</td>
              <td>Description</td>
              <td style={{ textAlign: "right" }}>Total Amount</td>
            </tr>

            {details.map((row, index) => (
              <tr key={row.id || index}>
                <td>{index + 1}</td>
                <td>{row.exp_code}</td>
                <td>{moment(row.created_date).format("YYYY-MM-DD")}</td>
                <td>{row.acc_name}</td>
                <td>{row.full_name}</td>
                <td>{row.narration}</td>
                <td style={{ textAlign: "right" }}>
                  {format(parseFloat(row.exp_amount || 0).toFixed(2))}
                </td>
              </tr>
            ))}

            <tr className="ledger-total-row">
              <td colSpan={6} style={{ textAlign: "right" }}>
                Grand Total
              </td>
              <td style={{ textAlign: "right" }}>{format(totals.toFixed(2))}</td>
            </tr>
          </tbody>
        </table>
      );
    } else if (selectedRecordType?.recordType === "with details") {
      const rows = buildWithDetailsData(filterResultDetails);
      const grandTotal = filterResultDetails.reduce(
        (sum, item) => sum + parseFloat(item.exp_total || 0),
        0
      );

      return (
        <table className="ledger-print-table">
          <colgroup>
            <col style={{ width: "4%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "9%" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "19%" }} />
            <col style={{ width: "15%" }} />
          </colgroup>
          <tbody>
            <tr className="ledger-print-heading">
              <td>No.</td>
              <td>VCH</td>
              <td>Date</td>
              <td>Payment From</td>
              <td>Payment By</td>
              <td>Payment For</td>
              <td>Description</td>
              <td style={{ textAlign: "right" }}>Payment Amount</td>
            </tr>

            {rows.map((row) =>
              row.isSubtotal ? (
                <tr className="ledger-opening-row" key={row.key}>
                  <td colSpan={7} style={{ textAlign: "right" }}>
                    Sub Total
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {format(row.subTotal.toFixed(2))}
                  </td>
                </tr>
              ) : (
                <tr key={row.key}>
                  <td>{row.isFirstDetail ? row.voucherNo : ""}</td>
                  <td>{row.exp_code}</td>
                  <td>{moment(row.created_date).format("YYYY-MM-DD")}</td>
                  <td>{row.acc_name}</td>
                  <td>{row.full_name}</td>
                  <td>{row.paymentFor}</td>
                  <td>{row.narration}</td>
                  <td style={{ textAlign: "right" }}>
                    {format(parseFloat(row.exp_amount || 0).toFixed(2))}
                  </td>
                </tr>
              )
            )}

            <tr className="ledger-total-row">
              <td colSpan={7} style={{ textAlign: "right" }}>
                Grand Total
              </td>
              <td style={{ textAlign: "right" }}>{format(grandTotal.toFixed(2))}</td>
            </tr>
          </tbody>
        </table>
      );
    } else {
      const totals = filterResult.reduce(
        (sum, item) => sum + parseFloat(item.exp_total || 0),
        0
      );

      return (
        <table className="ledger-print-table">
          <colgroup>
            <col style={{ width: "4%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "9%" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "22%" }} />
            <col style={{ width: "15%" }} />
          </colgroup>
          <tbody>
            <tr className="ledger-print-heading">
              <td>No.</td>
              <td>VCH No</td>
              <td>Date</td>
              <td>Payment From</td>
              <td>Pay To</td>
              <td>Payment By</td>
              <td>Description</td>
              <td style={{ textAlign: "right" }}>Total Amount</td>
            </tr>

            {filterResult.map((row, index) => (
              <tr key={row.id || index}>
                <td>{index + 1}</td>
                <td>{row.exp_code}</td>
                <td>{moment(row.created_date).format("YYYY-MM-DD")}</td>
                <td>{row.acc_name}</td>
                <td>{row.pay_to}</td>
                <td>{row.full_name}</td>
                <td>{row.narration}</td>
                <td style={{ textAlign: "right" }}>
                  {format(parseFloat(row.exp_total || 0).toFixed(2))}
                </td>
              </tr>
            ))}

            <tr className="ledger-total-row">
              <td colSpan={7} style={{ textAlign: "right" }}>
                Grand Total
              </td>
              <td style={{ textAlign: "right" }}>{format(totals.toFixed(2))}</td>
            </tr>
          </tbody>
        </table>
      );
    }
  };

  return (
    <div ref={ref} style={{ padding: 20 }}>
      <InstitutionProfile />

      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <Title level={3}>Expense Record</Title>
      </div>

      <div style={{ marginBottom: 20 }}>
        <div style={{ float: "left", width: "50%" }}>
          <Text strong>
            {selectedFilterType ? `Filter Type: ${selectedFilterType.filterType}` : ""}
          </Text>
          <br />
          {selectedFilterType?.filterType === "By Expense" && (
            <>
              <Text strong>Expense Name: </Text>{filters.selectedExpense?.acc_name || ""}<br />
              <Text strong>Institution: </Text>{filters.selectedExpense?.institution_name || ""}<br />
              <Text strong>Address: </Text>{filters.selectedExpense?.address || ""}<br />
              <Text strong>Contact No: </Text>{filters.selectedExpense?.contact_no || ""}<br />
            </>
          )}
          {selectedFilterType?.filterType === "By User" && (
            <>
              <Text strong>User: </Text>{filters.selectedUser?.name || ""}<br />
            </>
          )}
        </div>

        <div style={{ float: "right", width: "40%", textAlign: "right" }}>
          <Text strong>
            Statement: {filters.fromDate && toCompanyDateOnlyforReportDateFilter(filters.fromDate).format("YYYY-MM-DD")} - {filters.toDate && toCompanyDateOnlyforReportDateFilter(filters.toDate).format("YYYY-MM-DD")}
          </Text>
        </div>

        <div style={{ clear: "both" }}></div>
      </div>

      {renderPrintContent()}

      <div style={{ marginTop: 50 }}>
        <div style={{ float: "left", width: 150, borderTop: "1px solid #000", textAlign: "center" }}>
          Received By
        </div>

        <div style={{ float: "right", width: 150, borderTop: "1px solid #000", textAlign: "center" }}>
          Authorized By
        </div>

        <div style={{ clear: "both" }}></div>
      </div>
    </div>
  );
});

export default ExpenseRecordPrintView;
